import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

// Chart.js / react-chartjs-2
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip as ChartTooltip,
  Legend,
  Title
} from "chart.js";

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip, Legend, Title);

// === Colors (your palette) ===
const PRIMARY = "#9848c7";                       // current year = purple
const PRIMARY_BORDER = "rgba(152, 72, 199, 1)";
const PRIMARY_LIGHT = "rgba(152, 72, 199, 0.2)"; // light purple accents
const RED = "rgba(220, 53, 69, 0.75)";           // previous year = red
const RED_BORDER = "rgba(220, 53, 69, 1)";

const Wrapper = styled.div`
  position: relative;
  padding: 1rem;
  height: 100%;          /* allow the card to expand to its parent */
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TitleRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-weight: bold;
  font-size: 12px;
  min-height: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  right: 10px;
  top: 0;
  cursor: pointer;
  color: #666;
  transition: color 0.2s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Filters = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
`;

const ChartBox = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);
  padding: 12px;

  /* Fill all available space in the card */
  flex: 1 1 auto;
  min-height: 360px;

  display: flex;
  flex-direction: column;
`;

const CanvasWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;   /* the Bar chart will stretch to fill */
`;

const monthNamesPL = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień"
];

const safeNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const calculatePurchases = (row) => Math.max(0, safeNumber(row.K_42 || row.K_43 || 0));

/**
 * Chart-only report: Zakupy według miesięcy
 * - Bar chart (current vs previous year)
 * - Fills entire card area
 */
const ZakupyWedlugMiesiecyChart = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  // derive available years
  useEffect(() => {
    const years = new Set();
    (data || []).forEach((row) => {
      const y = parseInt(row.Rok, 10);
      if (!isNaN(y)) years.add(y);
    });
    const sorted = Array.from(years).sort((a, b) => a - b);
    setAvailableYears(sorted);
    if (!selectedYear && sorted.length > 0) {
      setSelectedYear(sorted[sorted.length - 1]);
    }
  }, [data]);

  const chartData = useMemo(() => {
    if (!selectedYear) return null;

    // aggregate sums per month
    const grouped = {};
    (data || []).forEach((row) => {
      const y = parseInt(row.Rok, 10);
      const m = parseInt(row.Miesiac, 10);
      if (!grouped[y]) grouped[y] = {};
      if (!grouped[y][m]) grouped[y][m] = 0;
      grouped[y][m] += calculatePurchases(row);
    });

    const currentVals = [];
    const prevVals = [];
    for (let m = 1; m <= 12; m++) {
      currentVals.push(grouped[selectedYear]?.[m] ?? 0);
      prevVals.push(grouped[selectedYear - 1]?.[m] ?? 0);
    }

    return {
      labels: monthNamesPL,
      datasets: [
        {
          label: `Zakupy (${selectedYear})`,
          data: currentVals,
          backgroundColor: "rgba(152, 72, 199, 0.85)",
          borderColor: PRIMARY_BORDER,
          borderWidth: 1,
          order: 2
        },
        {
          label: `Zakupy (${selectedYear - 1})`,
          data: prevVals,
          backgroundColor: RED,
          borderColor: RED_BORDER,
          borderWidth: 1,
          order: 1
        }
      ]
    };
  }, [data, selectedYear]);

  if (!selectedYear) return null;

  return (
    <Wrapper>
      <TitleRow>
        Zakupy według miesięcy – {selectedYear}
        <InfoIconWrapper data-tooltip-id="zakupy-monthly-only-tooltip">
          <Info />
        </InfoIconWrapper>
      </TitleRow>

      <InfoTooltip
        id="zakupy-monthly-only-tooltip"
        place="right"
        content={
          <>
            <strong>Zakupy według miesięcy</strong>
            <br />
            Wartości zakupów w miesiącach roku {selectedYear} (fiolet) vs {selectedYear - 1} (czerwony).
          </>
        }
      />

      <Filters>
        <label>
          Rok:{" "}
          <select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </Filters>

      <ChartBox>
        <CanvasWrap>
          {chartData && (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false, // IMPORTANT for full height
                plugins: {
                  title: {
                    display: true,
                    text: `Zakupy według miesięcy – ${selectedYear}`
                  },
                  legend: { position: "top" },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const v = ctx.parsed.y ?? 0;
                        return `${ctx.dataset.label}: ${v.toLocaleString("pl-PL", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: { color: "#333" }
                  },
                  y: {
                    beginAtZero: true,
                    title: { display: true, text: "Zakupy (PLN)" },
                    grid: { color: PRIMARY_LIGHT },
                    ticks: {
                      callback: (val) =>
                        Number(val).toLocaleString("pl-PL", { minimumFractionDigits: 0 })
                    }
                  }
                }
              }}
            />
          )}
        </CanvasWrap>
      </ChartBox>
    </Wrapper>
  );
};

export default ZakupyWedlugMiesiecyChart;
